import React, {
  createContext,
  useCallback,
  useEffect,
  useContext,
  useState,
  ReactNode,
} from 'react'

import { cloneDeep, forOwn, get, isArray, isFunction, isObject, set, unset } from 'lodash'
import * as yup from 'yup'

type Callback = () => void
type IdData = { [name: string]: number | number[] }
type FieldData = { [name: string]: any }
type Setter = (name: string, value: any, cb?: () => void) => void
type Remover = (name: string, cb?: () => void) => void
type Getter = (name: string) => any
type Submitter = (data: any) => void
type Transformer = (value: any) => any

const removeIndex = (array: any, index: number) => {
  if (isArray(array)) {
    array.splice(index, 1)
  }
}

class FormData {
  nextId: number
  ids: IdData
  values: FieldData
  subs: { [name: string]: Callback | Callback[] }
  errors: FieldData
  submitTransformers: { [name: string]: Transformer | Transformer[] }

  constructor() {
    this.nextId = 0
    this.ids = {}
    this.values = {}
    this.subs = {}
    this.errors = {} // mirrors values
    this.submitTransformers = {} // mirrors values
  }

  setSubmitTransformer = (name: string, fn: (value: any) => any) => {
    set(this.submitTransformers, name, fn)
  }

  getSubmitValues = () => {
    /* Copy this.values converting values based on what is in
     * this.submitTransformers
     */
    const toSubmit = cloneDeep(this.values)
    const applyTransforms = (obj: any | any[], path: string) => {
      const transform = get(this.submitTransformers, path)
      if (isFunction(transform)) {
        set(toSubmit, path, transform(obj))
      } else if (!isFunction(obj)) {
        if (isArray(obj)) {
          obj.map((item: any, index: number) => applyTransforms(item, `${path}[${index}]`))
        } else if (isObject(obj)) {
          forOwn(obj, (value, key) => {
            applyTransforms(value, `${path}${path !== '' ? '.' : ''}${key}`)
          })
        }
      }
    }
    applyTransforms(this.values, '')
    return toSubmit
  }

  setValue = (name: string, value: any, cb?: () => void) => {
    if (isArray(value)) {
      const ids = value.map(() => this.nextId++)
      set(this.ids, name, ids)
    }
    set(this.values, name, value)
    const func = get(this.subs, name)
    if (isFunction(func)) {
      ;(func as Callback)()
    }
    const listSub = `${name}_list`
    const list = get(this.subs, listSub)
    if (list) {
      ;(list as Callback)()
    }
    if (cb) {
      cb()
    }
  }

  getValue = (name: string) => {
    return get(this.values, name, null)
  }

  setError = (name: string, message: string) => {
    set(this.errors, name, message)
    this.notify(name)
  }

  getError = (name: string) => {
    return get(this.errors, name)
  }

  resetErrors = () => {
    this.errors = {}
    const notifyAll = (obj: any, path: string) => {
      if (isFunction(obj)) {
        obj()
      } else if (isObject(obj) && !isArray(obj)) {
        forOwn(obj, (value, key) => {
          notifyAll(value, `${path}${path !== '' ? '.' : ''}${key}`)
        })
      }
    }
    notifyAll(this.subs, '')
  }

  append = (name: string, data: any) => {
    const idArray = get(this.ids, name) as number[]
    const array = get(this.values, name)
    idArray.push(this.nextId++)
    array.push(data)
    this.notify(name)
  }

  extend = (name: string, data: any[]) => {
    const idArray = get(this.ids, name) as number[]
    const array = get(this.values, name)
    data.map((item) => {
      idArray.push(this.nextId++)
      array.push(item)
      return null
    })
    this.notify(name)
  }

  remove = (name: string, cb?: () => void) => {
    const match = name.match(/^(?<path>.+)\[(?<index>\d+)\]$/)
    if (!match?.groups || !match.groups.index) {
      return undefined
    }
    const listName = match.groups.path
    const index: number = +match.groups.index

    removeIndex(get(this.ids, listName), index)
    removeIndex(get(this.values, listName), index)
    removeIndex(get(this.subs, listName), index)
    removeIndex(get(this.errors, listName), index)
    removeIndex(get(this.submitTransformers, listName), index)

    this.notify(listName)

    if (cb) {
      cb()
    }
  }

  register = (name: string, fn: () => void) => {
    const subs = get(this.subs, name)
    if (!isArray(subs) && !isObject(subs)) {
      set(this.subs, name, fn)
    }
  }

  unregister = (name: string) => {
    const subs = get(this.subs, name)
    if (isFunction(subs)) {
      unset(this.subs, name)
    }
  }

  notify = (name: string) => {
    /* Notify subscriber if one exists */
    const fn = get(this.subs, name)
    const listFn = get(this.subs, `${name}_list`)
    if (isFunction(fn)) {
      fn()
    }
    if (isFunction(listFn)) {
      listFn()
    }
  }
}

type FieldRenderer = (params: {
  name: string
  value: any
  setValue: Setter
  remove: Remover
  error: string
  data: FieldData
}) => ReactNode
type FieldListRenderer = (params: {
  name: string
  value: any
  setValue: Setter
  error: string
  data: FieldData
  listName: string
  index: number
}) => ReactNode

const defaultDefaultRenderField: FieldRenderer = ({ name, value, setValue, error }) => {
  return (
    <>
      <input
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setValue(name, event.target.value)
        }
      />
      {error && <span>error</span>}
    </>
  )
}

interface FormContextProps {
  defaultRenderField: FieldRenderer
  data: FormData
  path: string
}

const FormContext = createContext<FormContextProps>({
  defaultRenderField: defaultDefaultRenderField,
  data: new FormData(),
  path: '',
})

interface FormProps {
  children: ReactNode
  defaultRenderField?: FieldRenderer
  formProps?: any
  onSubmit?: Submitter
}

interface FieldProps {
  name: string
  defaultValue?: any
  getSubmitValue?: (value: any) => any
  renderField?: FieldRenderer
}

export const Field = (props: FieldProps) => {
  const [, refresh] = useState({})
  const { defaultRenderField, data, path } = useContext(FormContext)
  const { defaultValue, name, renderField = defaultRenderField, getSubmitValue } = props

  const fullPath = `${path}${path !== '' ? '.' : ''}${name}`

  useEffect(() => {
    data.register(fullPath, () => refresh({}))
    if (getSubmitValue) {
      data.setSubmitTransformer(fullPath, getSubmitValue)
    }
    return () => {
      data.unregister(fullPath)
    }
  }, [data, fullPath, getSubmitValue])

  useEffect(() => {
    if (defaultValue !== undefined) {
      data.setValue(fullPath, defaultValue)
    }
  }, [defaultValue, data, fullPath])

  return (
    <FormContext.Provider
      value={{
        defaultRenderField,
        data,
        path: fullPath,
      }}
    >
      {renderField({
        name: fullPath,
        value: data.getValue(fullPath),
        setValue: data.setValue,
        remove: data.remove,
        error: data.getError(fullPath),
        data,
      })}
    </FormContext.Provider>
  )
}

interface RenderListProps {
  children: ReactNode
}

interface FieldListProps {
  name: string
  defaultValues?: any[]
  renderList?: React.FC<RenderListProps>
  renderHeader?: FieldRenderer
  renderField?: FieldListRenderer
  startCount?: number
  extra?: number
}

const FieldList = (props: FieldListProps) => {
  const [, refresh] = useState({})
  const { defaultRenderField, data, path } = useContext(FormContext)
  const {
    defaultValues,
    name,
    renderHeader,
    renderList,
    renderField = defaultRenderField,
    startCount = 1,
    extra = 0,
  } = props

  const ListWrapper = renderList || React.Fragment

  const fullPath = `${path}${path !== '' ? '.' : ''}${name}`

  useEffect(() => {
    data.register(`${fullPath}_list`, () => {
      refresh({})
    })
    return () => {
      data.unregister(`${fullPath}_list`)
    }
  }, [data, fullPath])

  useEffect(() => {
    const existingValue = data.getValue(fullPath)
    if (defaultValues !== undefined && !existingValue) {
      data.setValue(fullPath, defaultValues)
      if (extra > 0) {
        const additional = []
        for (let i = 0; i < extra; i++) {
          additional.push({})
        }
        data.extend(name, additional)
      }
    } else if (!existingValue) {
      const initial = []
      for (let i = 0; i < startCount; i++) {
        initial.push({})
      }
      data.setValue(fullPath, initial)
    }
  }, [defaultValues, data, extra, fullPath, name, startCount])

  const array = [...(data.getValue(name) || [])]

  return (
    <>
      {renderHeader &&
        renderHeader({
          name: fullPath,
          value: array,
          setValue: data.setValue,
          remove: data.remove,
          error: data.getError(fullPath),
          data,
        })}
      <ListWrapper>
        {array.map((value: any, index: number) => {
          const itemFullPath = `${fullPath}[${index}]`
          const id = get(data.ids, itemFullPath) as number
          return (
            <FormContext.Provider
              key={id}
              value={{
                defaultRenderField,
                data,
                path: itemFullPath,
              }}
            >
              {renderField({
                name: itemFullPath,
                value,
                setValue: data.setValue,
                remove: data.remove,
                error: data.getError(itemFullPath),
                data,
                listName: fullPath,
                index,
              })}
            </FormContext.Provider>
          )
        })}
      </ListWrapper>
    </>
  )
}

export const useForm = (schema?: yup.ObjectSchema) => {
  const [data, setData] = useState(new FormData())

  const Form = useCallback(
    (props: FormProps) => {
      const {
        children,
        formProps,
        defaultRenderField = defaultDefaultRenderField,
        onSubmit,
      } = props

      return (
        <form
          {...formProps}
          onSubmit={(e: React.SyntheticEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const toSubmit = data.getSubmitValues()
            data.resetErrors()
            if (schema) {
              schema
                .validate(toSubmit, { abortEarly: false })
                .then((values) => {
                  if (onSubmit) {
                    onSubmit(values)
                  }
                })
                .catch((errors) => {
                  errors.inner.map((err: { path: string; message: string }) =>
                    data.setError(err.path, err.message)
                  )
                })
            } else {
              if (onSubmit) {
                onSubmit(toSubmit)
              }
            }
          }}
        >
          <FormContext.Provider
            value={{
              defaultRenderField,
              data,
              path: '',
            }}
          >
            {children}
          </FormContext.Provider>
        </form>
      )
    },
    [data, schema]
  )

  const clear = useCallback(() => {
    setData(new FormData())
  }, [setData])

  return { Form, Field, FieldList, data, clear }
}
